
import React, { createContext, useContext } from 'react';
import { useSettings } from './SettingsContext';
import { en } from '../locales/en';
import { id } from '../locales/id';

type Language = 'en' | 'id';
type Translations = typeof en;

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  translations: Translations;
}

const translations: Record<Language, Translations> = {
  en,
  id
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const { language, setLanguage } = useSettings();
  
  // Validate that the language from settings is a valid language key
  const currentLanguage = (Object.keys(translations) as Language[]).includes(language as Language) 
    ? language as Language 
    : 'en';

  return (
    <LanguageContext.Provider value={{
      language: currentLanguage,
      setLanguage,
      translations: translations[currentLanguage]
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
